import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import * as ApiModels from '@typings/api-models';
import PaymentMethodLogo from '@components/Icons/PaymentMethodLogo';
import ProgressIndicator from '@components/ProgressIndicator';
import PaymentMethodEditModal from '@modals/PaymentMethodEditModal';
import PaymentMethodDeleteModal from '@modals/PaymentMethodDeleteModal';
import Buttons from '@components/Buttons/Buttons';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import s from './CardDetails.module.scss';

type Props = {
  card: {
    brand: Parameters<typeof PaymentMethodLogo>[0]['brand'] | null;
    exp_year?: number;
    exp_month?: number;
    last4: string;
  } | null;
  creator: Parameters<typeof PaymentMethodEditModal>[0]['creator'];
  isPrimary: boolean;
  isLoading?: boolean;
  onSaveCard?: Parameters<typeof PaymentMethodEditModal>[0]['onSaveCard'];
  paymentMethodId?: string;
  personId?: string;
  subscription?: {
    id: string;
    subscriptionStatus?: ApiModels.SubscriptionStatus | null;
  };
  retryPayment?: (id: string) => Promise<void>;
  onDeleteCard?: () => Promise<void>;
  defaultPaymentMethodId?: string;
  showSetAsDefaultOption?: boolean;
};

const CardDetails: React.FC<Props> = ({
  card,
  creator,
  isLoading,
  isPrimary,
  onSaveCard,
  paymentMethodId,
  personId,
  retryPayment,
  subscription,
  onDeleteCard,
  defaultPaymentMethodId,
  showSetAsDefaultOption,
}) => {
  const { getData: getPaymentPlansFn } = useNoodleApi(tsClient.paymentPlans.getPaymentPlansByPaymentMethodId);
  const { exp_month: expMonth, exp_year: expYear } = card || {};
  const expiredDate = `${expMonth}/${expYear?.toString().slice(-2)}`;
  let stateOfCard = null;

  const [isRetrying, setIsRetrying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isDeleteVisible, setIsDeleteVisible] = useState(false);
  const [hasActivePaymentPlans, setHasActivePaymentPlans] = useState(false);
  const [isCheckingPaymentPlans, setIsCheckingPaymentPlans] = useState(false);

  const isCardExpired = useMemo(() => {
    if (expMonth === undefined || expYear === undefined) {
      return true;
    }
    const expiration = new Date(expYear, expMonth);

    const today = new Date();

    return today > expiration;
  }, [expMonth, expYear]);

  if (isCardExpired) {
    stateOfCard = 'Expired';
  } else if (isPrimary) {
    stateOfCard = 'Primary';
  }

  const handleClick = (): void => {
    setIsVisible(true);
  };

  const handleRetry = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (subscription && retryPayment) {
      setIsRetrying(true);
      await retryPayment(subscription.id);
      setIsRetrying(false);
    }
  };

  const handleDeleteClick = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (paymentMethodId) {
      setIsCheckingPaymentPlans(true);
      try {
        const response = await getPaymentPlansFn({ paymentMethodId});
        const hasActive = response.data?.some((plan) => plan.status === 'active') || false;
        setHasActivePaymentPlans(hasActive);
      } catch (error) {
        console.error('Error checking payment plans:', error);
      } finally {
        setIsCheckingPaymentPlans(false);
      }
    }
    setIsDeleteVisible(true);
  };

  const getDeleteMessage = (): string => {
    if (hasActivePaymentPlans) {
      return 'Your bank account is on an active payment plan. To proceed, please add a new default payment method or cancel the active payment plan.';
    }
    if (isPrimary) {
      return 'You are about to delete your primary payment method. Are you sure?';
    }
    return 'You are about to delete the payment method. Are you sure?';
  };

  const handleDeleteSubmit = async (): Promise<void> => {
    setIsDeleteVisible(false);
    setHasActivePaymentPlans(false);
    if (onDeleteCard) {
      await onDeleteCard();
    }
  };

  const Component = onSaveCard ? 'button' : 'div';

  return (
    <>
      {onSaveCard && isVisible && personId && (
        <PaymentMethodEditModal
          creator={creator}
          onClose={() => setIsVisible(false)}
          onSaveCard={onSaveCard}
          personId={personId}
          isDefaultPaymentMethod={isPrimary}
          paymentMethodId={paymentMethodId}
          existingCard={card
            ? {
              brand: card.brand || 'unknown',
              exp_month: card.exp_month || 0,
              exp_year: card.exp_year || 0,
              last4: card.last4,
            }
            : null}
          defaultPaymentMethodId={defaultPaymentMethodId}
          showSetAsDefaultOption={showSetAsDefaultOption}
        />
      )}
      {isDeleteVisible && onDeleteCard &&(
        <PaymentMethodDeleteModal
          creatorId={creator.id}
          paymentMethodId={paymentMethodId || ''}
          cardLast4={card?.last4}
          onClose={() => {
            setIsDeleteVisible(false);
            setHasActivePaymentPlans(false);
          }}
          onSubmit={handleDeleteSubmit}
          message={getDeleteMessage()}
          isDeleteNeeded={!hasActivePaymentPlans}
        />
      )}
      <Component className={s.container} {...onSaveCard && { onClick: handleClick, style: { cursor: 'pointer' } }}>
        <PaymentMethodLogo brand={card?.brand || 'credit-card'} />
        <div className={s.content}>
          <p>{`•••• •••• •••• ${card?.last4}`}</p>
          {expMonth && expYear && (
            <small className={classNames(s['card-details'], { [s['card-details--expired']]: isCardExpired })}>
              {expiredDate} {stateOfCard && ' · '}
              {stateOfCard && (
                <span className={classNames(s['card-details--isPrimary'], { [s['card-details--isExpired']]: isCardExpired })}>
                  {isCardExpired ? 'Expired' : 'Primary'}
                </span>
              )}
            </small>
          )}
          {isLoading && <ProgressIndicator isAbsolute />}
        </div>
        <div style={{ display: 'flex', gap: '18px', justifyItems:'space-between' }}>
          {onDeleteCard && (
            <p style={{ color: 'red', cursor: isCheckingPaymentPlans ? 'default' : 'pointer' }} onClick={(paymentMethodId && !isCheckingPaymentPlans) ? handleDeleteClick : undefined}>
              {isCheckingPaymentPlans ? <ProgressIndicator size={16} isCentered /> : 'Delete'}
            </p>
          )}
          {onSaveCard && <p style={{ color: 'blue' }}>Edit</p>}
        </div>
        {subscription?.subscriptionStatus === ApiModels.SubscriptionStatus.PastDue && retryPayment && (
          <Buttons isFetching={isRetrying} className={s.retry} onClick={handleRetry}>
            Retry
          </Buttons>
        )}
      </Component>
    </>
  );
};

export default CardDetails;
